import {createContext, useState, FC} from "react";
import {useLocation} from "react-router-dom";
import {PlanSelectFormData} from "../types/planSelectForm";
import {clear as clearReservationCancel} from "../redux/reservationCancelSlice";
import {getBackSearchURL} from "../utils/search";
import dayjs from "dayjs";
import { useTranslation, Trans } from "react-i18next";
import {useEffect} from "react";
import {sessionConfig} from "../config/session";
import {
  getAdmitToken,
  isWaitingRoomOwningHeartbeat,
  sendHeartbeat,
  setCurrentPlanData,
  setDefaultOnAdmitRequired,
} from "../utils/waitingRoom";

export interface FormSessionType {
  checkSession: (data: PlanSelectFormData) => boolean;
  saveFormData: (formName: string, data: any) => void;
  loadFormData: (formName: string) => any | null;
  clearAllFormData: () => void;
}

const FormSessionContext = createContext<FormSessionType>({} as FormSessionType);

// セキュリティ対策: Form4（決済情報）はsessionStorageに保存しない
const ALLOWED_FORMS = ['form1', 'form2', 'form3', 'form5'];
const SESSION_KEY_PREFIX = 'ots_form_';

const FormSessionProvider: FC = ({children}) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const {idleMinutes, maxMinutes} = sessionConfig;
  const idleAlertMessage = t("しばらく操作がなかったためセッションタイムアウトが発生しました。お手数ですが、トップページから再度お手続きをしていただきますようお願いいたします。")
  const maxAlertMessage = t("お手続きの開始から一定時間が経過したため、セッションを終了しました。お手数ですが、トップページから再度お手続きをしていただきますようお願いいたします。")

  /**
   * 手続きセッションの生存確認。 issue #2992 / 報告書 §8 No.9
   *
   * ★2026-09-01 に「操作ベース（スライド方式）」へ変更した。
   *   従来は `planData.timestamp`（プランページ到達時刻・1回だけ設定）からの**絶対時間** 30 分で、
   *   混雑して各画面の待ちが延びると**普通に操作していても打ち切られた**（docs/99 隠れ仕様 #27）。
   *   alert の文言「しばらく操作がなかったため」も実装と一致していなかった。
   *
   *   - 通過するたびに `planData.timestamp` を「最後の操作時刻」として更新する（スライド）。
   *   - 手続き開始からの絶対上限 `maxMinutes` は別に持ち、**ここだけは延びない**
   *     （離席したまま在庫を抱え続ける利用者を切るため）。
   *
   * ★`planData` を直接書き換えている。呼び出し側は同じオブジェクト参照を `navigate` の state に
   *   渡すため、これで更新後の値がそのまま次の画面へ引き継がれる
   *   （このコードベースでは `planSelectFormData.data.priceData = ...` 等と同じ流儀）。
   */
  const checkSession = (planData: PlanSelectFormData) => {
    // ★入場制御の既定 401 ハンドラが戻り先を組み立てられるよう控える（§2-H）
    setCurrentPlanData({id: planData.id, searchParams: planData.searchParams});

    if (planData.id === undefined || planData.timestamp === undefined) {
      window.location.href = window.location.origin + '/';
      return false;
    }

    const now = dayjs();
    const lastActionAt = dayjs(planData.timestamp);
    const startedAtValue = planData.sessionStartedAt ?? planData.timestamp;
    const startedAt = dayjs(startedAtValue);

    // ★不正な日時（sessionStorage の破損・手で書き換えられた等）は差分が NaN になり、
    //   idle も絶対上限も「未満」と判定されて**両方すり抜ける**（issue #2992・レビュー指摘）。
    //   読めない時刻は失効として扱う。
    if (!lastActionAt.isValid() || !startedAt.isValid()) {
      alert(idleAlertMessage);
      window.location.href = getBackSearchURL(planData.searchParams, planData.id, i18n.language);
      return false;
    }

    // 最後の操作からの経過（スライドする側）
    const idleMin = now.diff(lastActionAt, "minute");
    if (idleMin >= idleMinutes) {
      alert(idleAlertMessage);
      window.location.href = getBackSearchURL(planData.searchParams, planData.id, i18n.language);
      return false;
    }

    // 手続き開始からの絶対上限。sessionStartedAt が無い＝この変更より前に開始した
    // セッションなので timestamp で代用する（打ち切りが早まらないよう安全側に倒す）
    if (now.diff(startedAt, "minute") >= maxMinutes) {
      alert(maxAlertMessage);
      window.location.href = getBackSearchURL(planData.searchParams, planData.id, i18n.language);
      return false;
    }

    // 通過＝操作あり。最後の操作時刻を更新する（スライド）
    planData.timestamp = now.format();
    return true;
  }

  /**
   * フォームデータをsessionStorageに保存
   * セキュリティ対策: Form4（決済情報）は保存しない
   */
  const saveFormData = (formName: string, data: any) => {
    if (!ALLOWED_FORMS.includes(formName)) {
      console.warn(`[FormSession] Security: ${formName} is not allowed for sessionStorage`);
      return;
    }

    try {
      const key = SESSION_KEY_PREFIX + formName;
      sessionStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('[FormSession] sessionStorage save error:', error);
    }
  };

  /**
   * フォームデータをsessionStorageから読み込み
   */
  const loadFormData = (formName: string): any | null => {
    if (!ALLOWED_FORMS.includes(formName)) {
      return null;
    }

    try {
      const key = SESSION_KEY_PREFIX + formName;
      const data = sessionStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('[FormSession] sessionStorage load error:', error);
      return null;
    }
  };

  /**
   * 全フォームデータをsessionStorageからクリア
   * 予約完了時に呼び出される
   */
  const clearAllFormData = () => {
    try {
      ALLOWED_FORMS.forEach(formName => {
        const key = SESSION_KEY_PREFIX + formName;
        sessionStorage.removeItem(key);
      });
    } catch (error) {
      console.error('[FormSession] sessionStorage clear error:', error);
    }
  };

  /**
   * 入場制御（待合室）の**既定の 401 ハンドラ**と滞在通知。 issue #2992 / 2026-09-03 レビュー §2-H
   *
   * ★プランページ以外（Form4 の `/price`、Form5 の `/payment`・`/reserve`、確認画面の
   *   `/plan/option`）で `ADMIT_REQUIRED` を受けると、以前は**トークンだけ消えて復帰できなかった**
   *   （ハンドラも heartbeat も `PlanSelectTop` の `useWaitingRoom` にしか無かった）。
   *   ON に切り替えた時点で手続き中だった利用者がそのまま詰む。
   *   ここで全画面共通の受け皿を用意し、**入力（sessionStorage）を保ったまま**
   *   プランページへ戻す。戻り先の `useWaitingRoom` が `expiredToken` 付きで priority 再入場する。
   */
  /**
   * 入場制御の既定 401 ハンドラが使う**戻り先**を、画面遷移・リロードのたびに記録する。
   * issue #2992 / 2026-09-04
   *
   * ★`checkSession` の中だけで記録していたため、**リロード直後は控えが空**だった。
   *   Form4 の `checkSession` は「次へ」を押したときにしか走らず、
   *   リロード直後に `/price` が 401 になると控えが無いまま**トップへ戻され**、
   *   トップはセッションを消すので「入力を保ったまま戻す」目的が達成できなかった。
   *   各画面は `navigate(..., {state: {data: planSelectFormData}})` で遷移してくるので、
   *   `location.state.data` から拾える。
   */
  useEffect(() => {
    const data = (location.state as {data?: PlanSelectFormData} | null | undefined)?.data;
    if (data?.id) {
      setCurrentPlanData({id: data.id, searchParams: data.searchParams});
    }
  }, [location]);

  useEffect(() => {
    setDefaultOnAdmitRequired((planData) => {
      if (planData?.id) {
        window.location.href = getBackSearchURL(
            planData.searchParams as PlanSelectFormData["searchParams"], planData.id, i18n.language);
        return;
      }
      window.location.href = window.location.origin + "/";
    });
    return () => setDefaultOnAdmitRequired(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  /**
   * 滞在通知（heartbeat）。★プランページ以外でも送る（§2-H）。
   * 送らないと手続き中の利用者が `maxActive` の集計から抜け、
   * 実際より空いていると誤認して入場レートが上がってしまう。
   */
  useEffect(() => {
    const send = () => {
      // ★プランページ（`useWaitingRoom`）がマウントされている間は送らない（2026-09-06 再レビュー）。
      //   2 本送ると、無操作でも Provider 側が枠を維持し続けてしまう
      if (isWaitingRoomOwningHeartbeat()) {
        return;
      }
      if (getAdmitToken()) {
        sendHeartbeat();
      }
    };
    send();
    const id = setInterval(send, sessionConfig.heartbeatMinutes * 60000);
    return () => clearInterval(id);
  }, []);

  return (
    <>
      <FormSessionContext.Provider
        value={{
          checkSession,
          saveFormData,
          loadFormData,
          clearAllFormData
        }}
      >
        {children}
      </FormSessionContext.Provider>
    </>
  );
}

export {FormSessionContext, FormSessionProvider}